import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Heart, CheckCircle, BarChart3, TrendingUp, Briefcase, Calendar, ClipboardCheck, Grid, Calculator, Bookmark, BookmarkCheck, Loader2, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useCasesAPI, savedUseCasesAPI, analyticsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const UseCaseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t, language } = useLanguage();
  
  const [useCase, setUseCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voted, setVoted] = useState(false);
  const [voteCount, setVoteCount] = useState(0);
  const [isVoting, setIsVoting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveCount, setSaveCount] = useState(0);
  
  // Analytics tracking refs
  const viewIdRef = useRef(null);
  const startTimeRef = useRef(null);

  // Helper to get translated text
  const getTranslatedText = (uc, field) => {
    if (!uc) return '';
    const nlField = `${field}_nl`;
    if (language === 'nl' && uc[nlField]) {
      return uc[nlField];
    }
    return uc[field] || '';
  };

  // Fetch use case from API
  useEffect(() => {
    const fetchUseCase = async () => {
      setLoading(true);
      try {
        const data = await useCasesAPI.getById(id);
        if (data) {
          // Add default fields that might be missing
          const enrichedData = {
            ...data,
            domainLabel: data.domainLabel || data.domain,
            implementationSteps: data.implementationSteps || [
              'Define scope and objectives',
              'Select appropriate AI tools',
              'Prepare and clean data',
              'Train and validate model',
              'Deploy and monitor'
            ],
            exampleTools: data.tools || data.exampleTools || ['Azure AI', 'AWS SageMaker', 'Google Cloud AI'],
            expectedOutcomes: data.expectedOutcomes || [
              'Improved efficiency',
              'Better decision making',
              'Cost reduction'
            ]
          };
          setUseCase(enrichedData);
          setVoteCount(data.votes || 0);
        }
      } catch (error) {
        console.error('Failed to fetch use case:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUseCase();
    
    // Check localStorage for previous vote
    const votedCases = JSON.parse(localStorage.getItem('votedUseCases') || '[]');
    setVoted(votedCases.includes(id) || votedCases.includes(parseInt(id)));
  }, [id]);

  // Check if use case is saved and get save count
  useEffect(() => {
    const fetchSaveData = async () => {
      if (!id) return;
      
      try {
        // Get save count (public endpoint)
        const countResult = await savedUseCasesAPI.getSaveCount(id);
        setSaveCount(countResult.save_count || 0);
        
        // Check if current user saved it
        if (isAuthenticated) {
          const result = await savedUseCasesAPI.checkSaved(id);
          setIsSaved(result.saved);
        }
      } catch (error) {
        console.error('Failed to fetch save data:', error);
      }
    };
    fetchSaveData();
  }, [isAuthenticated, id]);

  // Analytics: Track page view and time spent
  useEffect(() => {
    if (!useCase || !id) return;
    
    startTimeRef.current = Date.now();
    
    // Generate or get session ID
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    
    // Track view start
    const trackView = async () => {
      const result = await analyticsAPI.trackView({
        use_case_id: id,
        use_case_title: useCase.title,
        user_id: user?.id || null,
        user_email: user?.email || null,
        session_id: sessionId,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent
      });
      if (result?.view_id) {
        viewIdRef.current = result.view_id;
      }
    };
    trackView();
    
    // Track view end when leaving page
    const handleBeforeUnload = () => {
      if (viewIdRef.current && startTimeRef.current) {
        const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
        // Use sendBeacon for reliable tracking on page unload
        const data = JSON.stringify({
          view_id: viewIdRef.current,
          time_spent_seconds: timeSpent
        });
        navigator.sendBeacon(
          `${process.env.REACT_APP_BACKEND_URL}/api/analytics/track/view/end`,
          new Blob([data], { type: 'application/json' })
        );
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also track on component unmount (navigation within app)
      if (viewIdRef.current && startTimeRef.current) {
        const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
        analyticsAPI.trackViewEnd(viewIdRef.current, timeSpent);
      }
    };
  }, [useCase, id, user]);

  const handleSaveToggle = async () => {
    if (!isAuthenticated || isSaving) return;
    setIsSaving(true);
    
    try {
      if (isSaved) {
        await savedUseCasesAPI.unsave(id);
        setIsSaved(false);
        setSaveCount(prev => Math.max(0, prev - 1));
      } else {
        await savedUseCasesAPI.save(id);
        setIsSaved(true);
        setSaveCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Failed to toggle save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
            <p className="text-gray-600 mt-4">{t('loadingUseCase')}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!useCase) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-2xl font-bold text-gray-900">{t('useCaseNotFound')}</h1>
            <Button onClick={() => navigate('/use-cases')} className="mt-4">
              {t('backToUseCases')}
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleVote = async () => {
    if (isVoting) return;
    setIsVoting(true);
    
    try {
      const votedCases = JSON.parse(localStorage.getItem('votedUseCases') || '[]');
      
      if (voted) {
        const result = await useCasesAPI.unvote(id);
        setVoteCount(result.votes);
        setVoted(false);
        const newVotedCases = votedCases.filter(caseId => caseId !== id && caseId !== parseInt(id));
        localStorage.setItem('votedUseCases', JSON.stringify(newVotedCases));
      } else {
        const result = await useCasesAPI.vote(id);
        setVoteCount(result.votes);
        setVoted(true);
        votedCases.push(id);
        localStorage.setItem('votedUseCases', JSON.stringify(votedCases));
      }
    } catch (error) {
      console.error('Failed to vote:', error);
    } finally {
      setIsVoting(false);
    }
  };

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      low: language === 'nl' ? 'Lage Complexiteit' : 'Low Complexity',
      medium: language === 'nl' ? 'Gemiddelde Complexiteit' : 'Medium Complexity',
      high: language === 'nl' ? 'Hoge Complexiteit' : 'High Complexity'
    };
    return labels[difficulty] || difficulty;
  };

  const getImpactLabel = (impact) => {
    const labels = {
      low: language === 'nl' ? 'Lage' : 'Low',
      medium: language === 'nl' ? 'Gemiddelde' : 'Medium',
      high: language === 'nl' ? 'Hoge' : 'High'
    };
    return labels[impact] || impact;
  };

  const title = getTranslatedText(useCase, 'title');
  const description = getTranslatedText(useCase, 'description');
  const implementationSteps = language === 'nl' && useCase.implementationSteps_nl 
    ? useCase.implementationSteps_nl 
    : useCase.implementationSteps;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 mb-3">
                  {title}
                </h1>
                <p className="text-gray-600 text-lg mb-4">
                  {description}
                </p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    {getDifficultyLabel(useCase.difficulty)}
                  </span>
                  <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    {useCase.domainLabel || useCase.domain}
                  </span>
                  <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    {getImpactLabel(useCase.impact)} Impact
                  </span>
                </div>
              </div>
              
              {/* Vote & Save Buttons */}
              <div className="flex items-center gap-6 ml-8">
                {/* Save Button */}
                {isAuthenticated && (
                  <div className="text-center">
                    <button
                      onClick={handleSaveToggle}
                      disabled={isSaving}
                      className={`transition-all transform hover:scale-110 ${isSaved ? 'text-blue-500' : 'text-gray-400 hover:text-blue-500'} ${isSaving ? 'opacity-50 cursor-wait' : ''}`}
                      title={isSaved ? t('removeFromSaved') : t('saveToList')}
                      data-testid="save-usecase-btn"
                    >
                      {isSaving ? (
                        <Loader2 className="h-7 w-7 animate-spin" />
                      ) : isSaved ? (
                        <BookmarkCheck className="h-7 w-7 fill-current" />
                      ) : (
                        <Bookmark className="h-7 w-7" />
                      )}
                    </button>
                    <div className="text-2xl font-bold text-gray-900 mt-1">{saveCount}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 justify-center">
                      <Users className="h-3 w-3" />
                      {t('saved')}
                    </div>
                  </div>
                )}
                
                {/* Save count for non-authenticated users */}
                {!isAuthenticated && saveCount > 0 && (
                  <div className="text-center">
                    <div className="w-7 h-7 flex items-center justify-center text-gray-400">
                      <Bookmark className="h-7 w-7" />
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">{saveCount}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 justify-center">
                      <Users className="h-3 w-3" />
                      {t('saved')}
                    </div>
                  </div>
                )}
                
                {/* Vote Button */}
                <div className="text-center">
                  <button
                    onClick={handleVote}
                    disabled={isVoting}
                    className={`transition-all transform hover:scale-110 ${voted ? 'text-red-500' : 'text-gray-400 hover:text-red-500'} ${isVoting ? 'opacity-50 cursor-wait' : ''}`}
                    title={voted ? t('clickToRemoveVote') : t('clickToVote')}
                    data-testid="vote-usecase-btn"
                  >
                    <Heart className={`h-8 w-8 ${voted ? 'fill-current' : ''}`} />
                  </button>
                  <div className="text-2xl font-bold text-gray-900 mt-1">{voteCount}</div>
                  <div className="text-sm text-gray-500">{t('votes')}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Implementation Steps */}
              {implementationSteps && implementationSteps.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5 text-blue-500" />
                    {t('implementationSteps')}
                  </h2>
                  <div className="space-y-3">
                    {implementationSteps.map((step, index) => (
                      <div key={typeof step === 'string' ? step : `step-${index}`} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {index + 1}
                        </span>
                        <span className="text-gray-700 pt-0.5">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Example Tools */}
              {useCase.exampleTools && useCase.exampleTools.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-blue-500" />
                    {t('exampleTools')}
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {useCase.exampleTools.map((tool, index) => (
                      <span
                        key={typeof tool === 'string' ? tool : `tool-${index}`}
                        className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Expected Outcomes */}
              {useCase.expectedOutcomes && useCase.expectedOutcomes.length > 0 && (
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    {t('expectedOutcomes')}
                  </h2>
                  <div className="space-y-3">
                    {useCase.expectedOutcomes.map((outcome, index) => (
                      <div key={typeof outcome === 'string' ? outcome : `out-${index}`} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{outcome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Quick Info Card */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h3 className="font-bold text-gray-900 mb-4">{t('quickInfo')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('implementationTime')}</p>
                      <p className="font-semibold text-gray-900">{useCase.impl_time || useCase.implementationTime || 'N/A'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Grid className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('domainLabel')}</p>
                      <p className="font-semibold text-gray-900">{useCase.domainLabel || useCase.domain}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="h-5 w-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">{t('businessImpact')}</p>
                      <p className="font-semibold text-gray-900 capitalize">{getImpactLabel(useCase.impact)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions Card */}
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-sm p-6 text-white">
                <h3 className="font-bold mb-4">{t('readyToImplement')}</h3>
                <div className="space-y-3">
                  <Link to={`/use-cases/${id}/roi`}>
                    <Button variant="secondary" className="w-full bg-white text-blue-600 hover:bg-blue-50">
                      <Calculator className="h-4 w-4 mr-2" />
                      {t('calculateROI')}
                    </Button>
                  </Link>
                  <Link to="/workshop">
                    <Button variant="outline" className="w-full border-white text-white hover:bg-white/10 mt-2">
                      <Calendar className="h-4 w-4 mr-2" />
                      {t('bookWorkshop')}
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Back Button */}
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/use-cases')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('backToUseCases')}
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UseCaseDetailPage;
